import { KaboodleToaster } from "components/common";
import type {
  InvoiceLineItemType,
  InvoiceType,
} from "components/Invoicing/InvoiceTypes";
import { useCallback } from "react";
import {
  useCreateInvoiceLineItemMutation,
  useCreateInvoiceMutation,
  useDeleteInvoiceByIdMutation,
  useDeleteInvoiceLineItemMutation,
  useFinalizeInvoiceMutation,
  useGenerateDefaultLineItemMutation,
  useGetInvoicesQuery,
  useMarkAsManuallySentMutation,
  usePatchInvoiceByIdMutation,
  useUnmarkAsManuallySentMutation,
  useUpdateInvoiceLineItemMutation,
  useVoidAndReissueInvoiceMutation,
  useVoidInvoiceMutation,
} from "services/invoice-api";
import { extractErrorMessage } from "utils/error-utils";

import type { InvoiceableEntity } from "./InvoiceTypes";
import { InvoiceServiceType } from "./InvoiceTypes";
import useQuickBooksReconnect from "./QuickBooks/useQuickBooksReconnect";

type GetInvoicesParams = {
  file?: number;
  service?: number;
  client?: number;
  is_cms_invoice?: boolean;
  invoiceType?:
    | "file_service"
    | "section_111"
    | "future_medical"
    | "lien"
    | "document_management";
  skip?: boolean;
};

export const useInvoice = (params?: GetInvoicesParams) => {
  const [createInvoice] = useCreateInvoiceMutation();
  const [deleteInvoice] = useDeleteInvoiceByIdMutation();
  const [voidInvoice] = useVoidInvoiceMutation();
  const [voidAndReissueInvoice] = useVoidAndReissueInvoiceMutation();
  const [updateInvoice] = usePatchInvoiceByIdMutation();
  const [createLineItem] = useCreateInvoiceLineItemMutation();
  const [updateLineItem] = useUpdateInvoiceLineItemMutation();
  const [deleteLineItem] = useDeleteInvoiceLineItemMutation();
  const [finalizeInvoice] = useFinalizeInvoiceMutation();
  const [generateDefaultLineItem] = useGenerateDefaultLineItemMutation();
  const [markAsManuallySent] = useMarkAsManuallySentMutation();
  const [unmarkAsManuallySent] = useUnmarkAsManuallySentMutation();

  const quickBooksReconnect = useQuickBooksReconnect();

  const { data: invoices = [], ...invoicesQuery } = useGetInvoicesQuery(
    {
      ...params,
    },
    { skip: !params },
  );

  const handleCreateInvoice = useCallback(
    async (entity: InvoiceableEntity | null, isCMSInvoice?: boolean) => {
      if (!entity) {
        console.warn("useInvoice.handleCreateInvoice: entity is null");
        return;
      }

      try {
        const data: Partial<InvoiceType> = {
          organization: entity.organization,
          email_subject: "---",
          description: "---",
          is_cms_invoice: isCMSInvoice ?? false,
        };

        // set invoice_type based on service type
        if (
          entity.type === InvoiceServiceType.MSA ||
          entity.type === InvoiceServiceType.MSA_111 ||
          entity.type === InvoiceServiceType.NTMSA ||
          entity.type === InvoiceServiceType.MCP ||
          entity.type === InvoiceServiceType.PRE_MSA ||
          entity.type === InvoiceServiceType.AMENDED_REVIEW ||
          entity.type === InvoiceServiceType.AMENDED_REVIEW_SCREENING ||
          entity.type === InvoiceServiceType.LCP
        ) {
          data.invoice_type = "future_medical";
        } else if (entity.type === InvoiceServiceType.LIEN) {
          data.invoice_type = "lien";
        } else if (entity.type === InvoiceServiceType.DOCUMENT_MANAGEMENT) {
          data.invoice_type = "document_management";
        } else {
          data.invoice_type = "file_service";
        }

        // if entity has a file, add the file id to the invoice
        if (
          entity.file &&
          typeof entity.file === "object" &&
          "id" in entity.file
        ) {
          data.file = entity.file.id;
        }

        // if entity has a service, add the service id to the invoice
        if (entity.service) {
          data.service = entity.service;
        }

        // if entity has a client (should always be true), add the client id, email, and quickbooks_workspace to the invoice
        if (entity.client) {
          data.client = entity.client.id;

          // set quickbooks_workspace from client if available, otherwise will use backend default
          if (entity.client.quickbooks_workspace) {
            data.quickbooks_workspace = entity.client.quickbooks_workspace;
          }
        }

        return await createInvoice({
          data,
        });
      } catch (error) {
        console.error("Error creating invoice:", error);

        // extract specific error message from API response
        let errorMessage = "Failed to create invoice";
        if (error && typeof error === "object" && "data" in error) {
          const apiError = error as { data?: { error?: string } };
          if (apiError.data?.error) {
            errorMessage = apiError.data.error;
          }
        }

        KaboodleToaster.error(errorMessage);
        throw error;
      }
    },
    [createInvoice],
  );

  const handleDeleteInvoice = useCallback(
    async (invoiceId: number) => {
      if (!invoiceId) {
        console.warn("useInvoice.handleDeleteInvoice: invoiceId is falsy");
        return;
      }

      try {
        await deleteInvoice({ id: invoiceId });
      } catch (error) {
        console.error("Error deleting invoice:", error);
        KaboodleToaster.error("Failed to delete invoice");
        throw error;
      }
    },
    [deleteInvoice],
  );

  const handleVoidInvoice = useCallback(
    async (invoiceId: number) => {
      if (!invoiceId) {
        console.warn("useInvoice.handleVoidInvoice: invoiceId is falsy");
        return;
      }

      try {
        await KaboodleToaster.promiseLoading(
          voidInvoice({ id: invoiceId }).unwrap(),
          {
            loadingMessage: "Voiding invoice...",
            successMessage: "Invoice voided successfully",
            errorMessage: "Failed to void invoice",
          },
        );
      } catch (error) {
        console.error("Error voiding invoice:", error);

        // extract specific error message from API response
        let errorMessage = "Failed to void invoice";
        if (error && typeof error === "object" && "data" in error) {
          const apiError = error as { data?: { error?: string } };
          if (apiError.data?.error) {
            errorMessage = apiError.data.error;
          }
        }

        KaboodleToaster.error(errorMessage);
        throw error;
      }
    },
    [voidInvoice],
  );

  const handleVoidAndReissueInvoice = useCallback(
    async (invoiceId: number, reissue = true) => {
      if (!invoiceId) {
        console.warn(
          "useInvoice.handleVoidAndReissueInvoice: invoiceId is falsy",
        );
        return;
      }

      try {
        const result = await KaboodleToaster.promiseLoading(
          voidAndReissueInvoice({
            id: invoiceId,
            reissue,
          }).unwrap(),
          {
            loadingMessage: reissue
              ? "Voiding and reissuing invoice..."
              : "Voiding invoice...",
            successMessage: reissue
              ? "Invoice voided and reissued successfully"
              : "Invoice voided successfully",
            errorMessage: reissue
              ? "Failed to void and reissue invoice"
              : "Failed to void invoice",
          },
        );
        return result;
      } catch (error) {
        console.error("Error voiding and reissuing invoice:", error);

        // extract specific error message from API response
        let errorMessage = reissue
          ? "Failed to void and reissue invoice"
          : "Failed to void invoice";
        if (error && typeof error === "object" && "data" in error) {
          const apiError = error as { data?: { error?: string } };
          if (apiError.data?.error) {
            errorMessage = apiError.data.error;
          }
        }

        KaboodleToaster.error(errorMessage);
        throw error;
      }
    },
    [voidAndReissueInvoice],
  );

  const handleUpdateInvoice = useCallback(
    async (invoiceId: number, updates: Partial<InvoiceType>) => {
      if (!invoiceId) {
        console.warn("useInvoice.handleUpdateInvoice: invoiceId is falsy");
        return;
      }

      try {
        return await updateInvoice({
          id: invoiceId,
          data: updates,
        });
      } catch (error) {
        console.error("Error updating invoice:", error);
        KaboodleToaster.error("Failed to update invoice");
        throw error;
      }
    },
    [updateInvoice],
  );

  const handleCreateLineItem = useCallback(
    async (invoiceId: number) => {
      if (!invoiceId) {
        console.warn("useInvoice.handleCreateLineItem: invoiceId is falsy");
        return;
      }

      try {
        return await createLineItem({
          data: {
            invoice: invoiceId,
            rate: 1,
            quantity: 1,
            description: "---",
          },
        });
      } catch (error) {
        console.error("Error creating line item:", error);
        KaboodleToaster.error("Failed to create line item");
        throw error;
      }
    },
    [createLineItem],
  );

  const handleUpdateLineItem = useCallback(
    async (lineItemId: number, updates: Partial<InvoiceLineItemType>) => {
      if (!lineItemId) {
        console.warn("useInvoice.handleUpdateLineItem: lineItemId is falsy");
        return;
      }

      try {
        return await updateLineItem({
          id: lineItemId,
          data: updates,
        });
      } catch (error) {
        console.error("Error updating line item:", error);
        KaboodleToaster.error("Failed to update line item");
        throw error;
      }
    },
    [updateLineItem],
  );

  const handleDeleteLineItem = useCallback(
    async (lineItemId: number, invoiceId: number) => {
      if (!lineItemId || !invoiceId) {
        console.warn(
          "useInvoice.handleDeleteLineItem: lineItemId or invoiceId is falsy",
        );
        return;
      }

      try {
        await deleteLineItem({
          id: lineItemId,
          invoiceId,
        });
      } catch (error) {
        console.error("Error deleting line item:", error);
        KaboodleToaster.error("Failed to delete line item");
        throw error;
      }
    },
    [deleteLineItem],
  );

  const handleGenerateDefaultLineItem = useCallback(
    async (invoiceId: number, pricingFieldType: string) => {
      if (!invoiceId) {
        console.warn(
          "useInvoice.handleGenerateDefaultLineItem: invoiceId is falsy",
        );
        return;
      }

      try {
        const response = await generateDefaultLineItem({
          invoice_id: invoiceId,
          pricing_field: pricingFieldType,
        });

        return response;
      } catch (error) {
        console.error("Error generating default line item:", error);
        KaboodleToaster.error("Failed to generate default line item");
        throw error;
      }
    },
    [generateDefaultLineItem],
  );

  const handleFinalizeInvoice = useCallback(
    async (invoiceId: number, invoiceData?: Partial<InvoiceType>) => {
      if (!invoiceId) {
        console.warn("useInvoice.handleFinalizeInvoice: invoiceId is falsy");
        return;
      }

      try {
        // use promiseLoading to show toast notifications during the finalization process
        return await KaboodleToaster.promiseLoading(
          finalizeInvoice({
            id: invoiceId,
            data: invoiceData,
          }).unwrap(),
          {
            loadingMessage: "Finalizing invoice...",
            successMessage: "Invoice finalized successfully",
            errorMessage: "Failed to finalize invoice",
          },
        );
      } catch (error) {
        console.error("Error finalizing invoice:", error);

        const defaultErrorMessage = "Failed to finalize invoice";
        const extractedErrorMessage = extractErrorMessage(
          error,
          defaultErrorMessage,
        );
        const errorMessage =
          extractedErrorMessage === defaultErrorMessage
            ? defaultErrorMessage
            : `${defaultErrorMessage}: ${extractedErrorMessage}`;

        // use the QuickBooks reconnect handler to check if it's a QuickBooks auth error
        const isQuickBooksAuthError =
          await quickBooksReconnect.handleApiError(error);

        if (isQuickBooksAuthError) {
          // if it's a QuickBooks auth error, show a more specific message
          KaboodleToaster.success("Reconnecting to QuickBooks. Please wait...");
        } else {
          // show a base error plus backend-provided details when available
          KaboodleToaster.error(errorMessage);
        }

        // re-throw to maintain the same API behavior
        throw error;
      }
    },
    [finalizeInvoice, quickBooksReconnect],
  );

  const handleMarkAsManuallySent = useCallback(
    async (invoiceId: number) => {
      if (!invoiceId) {
        console.warn("useInvoice.handleMarkAsManuallySent: invoiceId is falsy");
        return;
      }

      try {
        return await KaboodleToaster.promiseLoading(
          markAsManuallySent({ id: invoiceId }).unwrap(),
          {
            loadingMessage: "Marking invoice as manually sent...",
            successMessage: "Invoice marked as manually sent",
            errorMessage: "Failed to mark invoice as manually sent",
          },
        );
      } catch (error) {
        console.error("Error marking invoice as manually sent:", error);

        // extract specific error message from API response
        let errorMessage = "Failed to mark invoice as manually sent";
        if (error && typeof error === "object" && "data" in error) {
          const apiError = error as { data?: { error?: string } };
          if (apiError.data?.error) {
            errorMessage = apiError.data.error;
          }
        }

        KaboodleToaster.error(errorMessage);
        throw error;
      }
    },
    [markAsManuallySent],
  );

  const handleUnmarkAsManuallySent = useCallback(
    async (invoiceId: number) => {
      if (!invoiceId) {
        console.warn(
          "useInvoice.handleUnmarkAsManuallySent: invoiceId is falsy",
        );
        return;
      }

      try {
        return await KaboodleToaster.promiseLoading(
          unmarkAsManuallySent({ id: invoiceId }).unwrap(),
          {
            loadingMessage: "Unmarking invoice as manually sent...",
            successMessage: "Invoice unmarked as manually sent",
            errorMessage: "Failed to unmark invoice as manually sent",
          },
        );
      } catch (error) {
        console.error("Error unmarking invoice as manually sent:", error);

        // extract specific error message from API response
        let errorMessage = "Failed to unmark invoice as manually sent";
        if (error && typeof error === "object" && "data" in error) {
          const apiError = error as { data?: { error?: string } };
          if (apiError.data?.error) {
            errorMessage = apiError.data.error;
          }
        }

        KaboodleToaster.error(errorMessage);
        throw error;
      }
    },
    [unmarkAsManuallySent],
  );

  return {
    createInvoice: handleCreateInvoice,
    deleteInvoice: handleDeleteInvoice,
    voidInvoice: handleVoidInvoice,
    voidAndReissueInvoice: handleVoidAndReissueInvoice,
    updateInvoice: handleUpdateInvoice,
    createLineItem: handleCreateLineItem,
    updateLineItem: handleUpdateLineItem,
    deleteLineItem: handleDeleteLineItem,
    generateDefaultLineItem: handleGenerateDefaultLineItem,
    finalizeInvoice: handleFinalizeInvoice,
    markAsManuallySent: handleMarkAsManuallySent,
    unmarkAsManuallySent: handleUnmarkAsManuallySent,
    invoices,
    invoicesQuery,
  };
};
